import { AlignmentType, Document, Packer, Paragraph, TextRun } from 'docx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Strip all HTML tags and return plain text, preserving paragraph breaks.
 */
export const htmlToPlainText = (html = '') => {
    if (!html) return '';
    // Plain text (no HTML markup): textContent of text nodes already preserves
    // \n, so just trim and return directly without the HTML parsing path.
    if (!/<[a-zA-Z]/i.test(html)) return html.trim();
    // Manually insert newlines for block-level elements BEFORE parsing —
    // innerText on a detached node doesn't compute layout, so paragraph breaks
    // are lost. This ensures <p>, <div>, <br>, headings all produce newlines.
    let processed = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<\/h[1-6]>/gi, '\n\n')
        .replace(/<\/li>/gi, '\n');
    const el = document.createElement('div');
    el.innerHTML = processed;
    el.querySelectorAll('style, script').forEach((node) => node.remove());
    // textContent is safe now that we've already injected \n markers
    const text = el.textContent || '';
    // Collapse 3+ consecutive newlines to 2, and trim
    return text.replace(/\n{3,}/g, '\n\n').trim();
};

/**
 * Normalise raw backend content (either plain text or an HTML string) into an
 * HTML fragment that can be safely set as innerHTML of a <div> and will render
 * with correct block-level structure.
 *
 * Three cases handled:
 *  1. Plain text (no HTML tags)  – each \n-separated line is wrapped in a <p>
 *     so the HTML renderer does not collapse whitespace into one line.
 *  2. Full HTML document (<!DOCTYPE html> / <html>…) – <head> styles are
 *     extracted and prepended to the body content so class-based CSS (e.g.
 *     display:block on EPUB elements) is not silently dropped by innerHTML.
 *  3. HTML fragment – returned as-is.
 */
const normalizeContent = (content) => {
    if (!content) return '';
    // Case 1 – plain text
    if (!/<[a-zA-Z]/i.test(content)) {
        return content
            .split('\n')
            .map((line) => {
                // Preserve blank lines by replacing empty lines with &nbsp;
                const text = line.trim() || '\u00A0';
                return '<p>' + text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</p>';
            })
            .join('');
    }
    // Case 2 – full HTML document
    if (/<html[\s>]/i.test(content) || /<!DOCTYPE\s+html/i.test(content)) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const headStyles = [...doc.querySelectorAll('head style')].map((s) => s.outerHTML).join('');
        return headStyles + doc.body.innerHTML;
    }
    // Case 3 – fragment
    return content;
};

/**
 * Preprocess an HTML string (or plain text) for download as a .html file.
 * Plain text is converted to a minimal HTML document with one <p> per line.
 * HTML content has empty <p> elements replaced with &nbsp; so they render
 * with proper line height in all browsers.
 */
export const htmlToHtmlContent = (html = '') => {
    if (!html) return '';
    if (!/<[a-zA-Z]/i.test(html)) {
        // Plain text: build a simple HTML document
        const body = html
            .split('\n')
            .map((line) => {
                const text = line.trim() || '\u00A0';
                return '<p>' + text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</p>';
            })
            .join('');
        return `<!DOCTYPE html><html><body style="font-family:sans-serif;max-width:800px;margin:40px auto;line-height:1.6">${body}</body></html>`;
    }
    return html.replace(/<p([^>]*)>(\s|&nbsp;)*<\/p>/gi, '<p$1>\u00A0</p>');
};

/**
 * Convert an HTML string to an array of docx Paragraph objects,
 * preserving bold/italic/underline formatting and text alignment.
 * Mirrors the logic that was originally inside TextEditor.js.
 */
export const htmlToDocxParagraphs = (html) => {
    const container = document.createElement('div');
    // Normalise: converts plain text → <p> blocks, full HTML docs → fragment
    // with head styles re-injected, fragments → unchanged.
    container.innerHTML = normalizeContent(html || '');
    const result = [];

    // Parse <style> blocks into a className → {prop: value} map so we can
    // resolve class-based styles (e.g. .block_ { font-weight:bold; text-align:center })
    // without needing getComputedStyle or attaching to the live DOM.
    const cssMap = {};
    container.querySelectorAll('style').forEach((styleEl) => {
        const cssText = (styleEl.textContent || '').replace(/\/\*[\s\S]*?\*\//g, '');
        const ruleRe = /([^{}@]+)\{([^{}]+)\}/g;
        let m;
        while ((m = ruleRe.exec(cssText)) !== null) {
            m[1].split(',').forEach((rawSelector) => {
                const sel = rawSelector.trim();
                if (!/^(\.[a-zA-Z_-][\w-]*)+$/.test(sel)) return;
                const key = sel.replace(/\./g, ' ').trim();
                if (!cssMap[key]) cssMap[key] = {};
                m[2].split(';').forEach((decl) => {
                    const ci = decl.indexOf(':');
                    if (ci === -1) return;
                    const prop = decl.slice(0, ci).trim();
                    const val = decl.slice(ci + 1).replace(/!important/gi, '').trim();
                    if (prop && val) {
                        const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                        cssMap[key][camel] = val;
                    }
                });
            });
        }
    });

    const getEffectiveStyle = (el) => {
        const styles = {};
        if (el.className && typeof el.className === 'string') {
            el.className.trim().split(/\s+/).forEach((cls) => {
                if (cls && cssMap[cls]) Object.assign(styles, cssMap[cls]);
            });
        }
        const s = el.style;
        if (s?.textAlign) styles.textAlign = s.textAlign;
        if (s?.fontWeight) styles.fontWeight = s.fontWeight;
        if (s?.fontStyle) styles.fontStyle = s.fontStyle;
        if (s?.textDecoration) styles.textDecoration = s.textDecoration;
        return styles;
    };

    const getAlignment = (el) => {
        const st = getEffectiveStyle(el);
        const align = st.textAlign || el.getAttribute?.('align') || '';
        switch (align.toLowerCase()) {
            case 'center':  return AlignmentType.CENTER;
            case 'right':
            case 'end':     return AlignmentType.RIGHT;
            case 'justify': return AlignmentType.JUSTIFIED;
            case 'left':
            case 'start':   return AlignmentType.LEFT;
            default:        return undefined;
        }
    };

    const fmtFromElement = (el) => {
        const fmt = {};
        const st = getEffectiveStyle(el);
        const fw = st.fontWeight;
        if (fw === 'bold' || parseInt(fw) >= 700) fmt.bold = true;
        if (st.fontStyle === 'italic') fmt.italics = true;
        if (st.textDecoration?.includes('underline')) fmt.underline = {};
        return fmt;
    };

    const collectRuns = (node, fmt = {}) => {
        const runs = [];
        for (const child of node.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent;
                if (text) runs.push(new TextRun({ text, ...fmt }));
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                const tag = child.tagName.toLowerCase();
                const childFmt = { ...fmt, ...fmtFromElement(child) };
                if (tag === 'b' || tag === 'strong') childFmt.bold = true;
                if (tag === 'i' || tag === 'em') childFmt.italics = true;
                if (tag === 'u') childFmt.underline = {};
                if (tag === 'br') {
                    runs.push(new TextRun({ text: '', break: 1 }));
                } else {
                    runs.push(...collectRuns(child, childFmt));
                }
            }
        }
        return runs;
    };

    const processNode = (node, listCtx = null, olCounterRef = { val: 0 }, inheritedAlignment = undefined) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent.trim();
            if (text) {
                const opts = { children: [new TextRun(text)] };
                if (inheritedAlignment) opts.alignment = inheritedAlignment;
                result.push(new Paragraph(opts));
            }
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        if (tag === 'style' || tag === 'script' || tag === 'head') return;
        if (tag === 'br') { result.push(new Paragraph({})); return; }

        if (tag === 'ul') {
            for (const child of node.childNodes) processNode(child, 'ul', olCounterRef, inheritedAlignment);
            return;
        }
        if (tag === 'ol') {
            const nestedRef = { val: 0 };
            for (const child of node.childNodes) processNode(child, 'ol', nestedRef, inheritedAlignment);
            return;
        }
        if (tag === 'li') {
            const runs = collectRuns(node, fmtFromElement(node));
            const alignment = getAlignment(node) || inheritedAlignment;
            if (listCtx === 'ol') {
                olCounterRef.val++;
                const opts = { children: [new TextRun({ text: `${olCounterRef.val}. ` }), ...runs] };
                if (alignment) opts.alignment = alignment;
                result.push(new Paragraph(opts));
            } else {
                const opts = { bullet: { level: 0 }, children: runs.length > 0 ? runs : [new TextRun('\u00A0')] };
                if (alignment) opts.alignment = alignment;
                result.push(new Paragraph(opts));
            }
            return;
        }
        if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
            const currentAlignment = getAlignment(node) || inheritedAlignment;
            const hasBlockChild = [...node.childNodes].some(
                (c) =>
                    c.nodeType === Node.ELEMENT_NODE &&
                    ['p', 'div', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(c.tagName.toLowerCase()),
            );
            if (hasBlockChild) {
                for (const child of node.childNodes) processNode(child, listCtx, olCounterRef, currentAlignment);
            } else {
                const runs = collectRuns(node, fmtFromElement(node));
                // Use non-breaking space for empty paragraphs so DOCX gives them
                // a proper line height (empty TextRun collapses to zero height).
                const opts = { children: runs.length > 0 ? runs : [new TextRun('\u00A0')] };
                if (currentAlignment) opts.alignment = currentAlignment;
                result.push(new Paragraph(opts));
            }
            return;
        }
        for (const child of node.childNodes) processNode(child, listCtx, olCounterRef, inheritedAlignment);
    };

    for (const child of container.childNodes) processNode(child);
    return result.length > 0 ? result : [new Paragraph({})];
};

/**
 * Convert an HTML string to a DOCX Blob.
 * Returns a Promise<Blob>.
 */
export const htmlToDocxBlob = async (html) => {
    const doc = new Document({
        sections: [{ properties: {}, children: htmlToDocxParagraphs(html) }],
    });
    return Packer.toBlob(doc);
};

/**
 * Fetch a file URL, convert it to the requested format, and trigger a browser download.
 * @param {string} fileUrl - URL to fetch HTML/text content from
 * @param {string} filename - base filename without extension
 * @param {'txt'|'pdf'|'docx'} format
 */
export const downloadTextFile = async (fileUrl, filename, format) => {
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error('Failed to fetch file');
    const content = await response.text();

    let blob;
    let ext;
    if (format === 'pdf') {
        blob = await htmlToPdfBlob(content);
        ext = 'pdf';
    } else if (format === 'docx') {
        blob = await htmlToDocxBlob(content);
        ext = 'docx';
    } else {
        blob = new Blob([htmlToPlainText(content)], { type: 'text/plain' });
        ext = 'txt';
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${ext}`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
};

/**
 * Render an HTML string to a PDF Blob using html2canvas + jsPDF.
 * Supports multi-page output when content is taller than one A4 page.
 * Returns a Promise<Blob>.
 */
export const htmlToPdfBlob = async (html) => {
    const container = document.createElement('div');
    container.style.cssText =
        'position:absolute;left:-9999px;top:0;width:794px;background:white;padding:40px;box-sizing:border-box;font-family:sans-serif;';
    // Normalise: plain text → <p> blocks, full doc → fragment with head styles,
    // fragment → unchanged. Then replace any remaining empty <p> (incl. &nbsp;
    // only) with an explicit &nbsp; so html2canvas gives them proper height.
    // Finally prepend a colour-reset so all text uses a single near-black tone.
    const normalized = normalizeContent(html || '');
    const preprocessed = normalized.replace(/<p([^>]*)>(\s|&nbsp;)*<\/p>/gi, '<p$1>\u00A0</p>');
    container.innerHTML = '<style>* { color: #1a1a1a !important; }</style>' + preprocessed;
    document.body.appendChild(container);
    try {
        const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        let remaining = imgHeight;
        let offset = 0;
        while (remaining > 0) {
            pdf.addImage(imgData, 'JPEG', 0, offset, pageWidth, imgHeight);
            remaining -= pageHeight;
            offset -= pageHeight;
            if (remaining > 0) pdf.addPage();
        }
        return pdf.output('blob');
    } finally {
        document.body.removeChild(container);
    }
};
